import { useEffect, useState } from 'react';
import type { FundManagementToolbarState } from './SimplifiedFundUpdatesAdminPanel';
import type { ToolbarStateChangeHandler } from './SimplifiedFundUpdatesAdminPanelSections';
import type { useFundUpdatesAdmin } from './useFundUpdatesAdmin';

type Controller = ReturnType<typeof useFundUpdatesAdmin>;
type StateChange = (
  state:
    | FundManagementToolbarState
    | null
    | ((prev: FundManagementToolbarState | null) => FundManagementToolbarState | null),
) => void;

export function useFundUpdatesPanelToolbar(
  controller: Controller,
  onToolbarStateChange?: StateChange,
) {
  const { state } = controller;
  const [openUpdateId, setOpenUpdateId] = useState<string | null>(null);
  useEffect(() => {
    if (state.loading) return;
    const stillExists = [...state.drafts, ...state.published].some(
      (u) => u.externalId === openUpdateId,
    );
    if (stillExists) return;
    setOpenUpdateId(state.drafts[0]?.externalId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading, state.drafts, state.published]);

  const openUpdate =
    [...state.drafts, ...state.published].find((u) => u.externalId === openUpdateId) ?? null;
  const handleToolbarStateChange: ToolbarStateChangeHandler = (editorState) => {
    if (!openUpdate) return;
    onToolbarStateChange?.((prev) => ({ ...prev, ...editorState }));
  };
  useEffect(() => {
    if (openUpdate) return;
    onToolbarStateChange?.((prev) =>
      prev
        ? {
            ...prev,
            saveStatus: undefined,
            pageMode: undefined,
            setPageMode: undefined,
            saveNow: undefined,
            toolbarRef: undefined,
          }
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUpdate]);

  useEffect(() => {
    const hasAnythingToPublishOrRevert =
      state.drafts.length > 0 || state.published.some((p) => p.hasPendingDraft);
    onToolbarStateChange?.((prev) => ({
      ...prev,
      onPublishAll: () => controller.handlePublishAll(),
      publishAllDisabled: !hasAnythingToPublishOrRevert || state.publishingAll,
      publishingAll: state.publishingAll,
      onRevertAll: () => controller.handleRevertAllToPublished(),
      revertAllDisabled: !hasAnythingToPublishOrRevert || state.revertingAll,
      revertingAll: state.revertingAll,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.drafts, state.published, state.publishingAll, state.revertingAll]);
  return { openUpdateId, setOpenUpdateId, handleToolbarStateChange };
}
